import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Scissors, Wrench } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner';
import type { GateAsset } from '@/hooks/useReviewGate';
import { apiFetch } from '@/lib/api';

interface Segment {
  segment_id?: string;
  source_asset_id?: string;
  in_s?: number;
  out_s?: number;
  /** "sync" (on-camera speech) | "broll" (visual, no dialogue) */
  role?: string;
  speaker?: string;
  text?: string;
  point?: string;
}

interface PaperCut {
  title?: string;
  sections?: { scene_number?: number; intent?: string; segments?: Segment[] }[];
  coverage_gaps?: { severity?: string; section?: string | number; missing?: string }[];
}

/** 72.5 → "1:12". */
const clock = (s: number) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
};

/**
 * The story before any pictures are cut — which soundbites are used, in what order,
 * and why. Notes can be left per section, or the whole cut rejected in one go.
 */
export default function PaperCutGate({
  productionId,
  assets,
  busy,
  onSectionChanges,
  onRejectAll,
}: {
  productionId: string;
  assets: GateAsset[];
  busy: boolean;
  onSectionChanges: (notes: Record<string, string>) => void;
  onRejectAll: (note: string) => void;
}) {
  const { t } = useTranslation('production');
  const [cut, setCut] = useState<PaperCut | null>(null);
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>({});
  const [overallNote, setOverallNote] = useState('');

  useEffect(() => {
    apiFetch(`/api/assets/${productionId}/paper_cut.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setCut)
      .catch(() => {
        /* display-only */
      });
  }, [productionId]);

  const noted = Object.values(sectionNotes).filter((v) => v.trim()).length;

  return (
    <div className="mt-3 space-y-3 border-t border-primary/25 pt-3">
      {!cut ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner /> {t('gate.papercut.loading')}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-sm font-semibold">
              <Scissors className="mr-1 inline h-4 w-4" />
              {cut.title || t('gate.papercut.title')}
            </div>
            {(() => {
              const sections = cut.sections || [];
              const total = sections.reduce(
                (t, s) => t + (s.segments || []).reduce((u, g) => u + Math.max(0, (g.out_s ?? 0) - (g.in_s ?? 0)), 0),
                0,
              );
              const nSeg = sections.reduce((n, s) => n + (s.segments?.length || 0), 0);
              return (
                <div className="text-xs text-faint">
                  {t('gate.papercut.summary', {
                    sections: t('papercut.sectionsCount', { count: sections.length }),
                    shots: t('papercut.shotsCount', { count: nSeg }),
                    runtime: clock(total),
                  })}
                </div>
              );
            })()}
          </div>

          {(cut.sections || []).map((sec, i) => {
            // Sections are gated in enumerate() order → assets[i] is this section.
            const assetId = assets[i]?.id;
            return (
              <div key={assetId || i} className="rounded-lg border border-primary/20 bg-background/40 p-2.5">
                <div className="mb-1.5 text-xs font-medium">
                  <Badge variant="secondary" className="mr-2 text-[10px]">
                    {t('papercut.section', { number: sec.scene_number ?? i + 1 })}
                  </Badge>
                  {sec.intent}
                </div>
                <div className="space-y-1.5">
                  {(sec.segments || []).map((seg, j) => {
                    const isBroll = (seg.role || '').toLowerCase() === 'broll';
                    const spoken = (seg.text || '').trim();
                    return (
                      <div key={seg.segment_id || j} className="flex items-start gap-2 text-[11px] leading-snug">
                        <span className="mt-px shrink-0 font-mono tabular-nums text-faint">
                          {clock(seg.in_s ?? 0)}–{clock(seg.out_s ?? 0)}
                        </span>
                        <span className="min-w-0 flex-1">
                          {isBroll ? (
                            <>
                              <Badge variant="outline" className="mr-1.5 border-sky-500/40 text-[8px] uppercase text-sky-500">
                                B-roll
                              </Badge>
                              <span className="text-muted-foreground">{seg.point || t('papercut.silentVisual')}</span>
                            </>
                          ) : spoken ? (
                            <>
                              {seg.speaker && <span className="font-semibold text-foreground">{seg.speaker}: </span>}
                              <span className="text-foreground">“{spoken}”</span>
                            </>
                          ) : (
                            <span className="italic text-muted-foreground">{seg.point || t('papercut.noTranscript')}</span>
                          )}
                          {seg.source_asset_id && (
                            <span className="ml-1 font-mono text-[9px] text-faint">
                              [{String(seg.source_asset_id).slice(0, 8)}]
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {assetId && (
                  <input
                    value={sectionNotes[assetId] || ''}
                    onChange={(e) => setSectionNotes((s) => ({ ...s, [assetId]: e.target.value }))}
                    placeholder={t('gate.papercut.sectionNote')}
                    className="mt-2 w-full rounded border border-border bg-background px-2 py-1 text-[11px] outline-none focus:border-primary"
                  />
                )}
              </div>
            );
          })}

          {(cut.coverage_gaps || []).length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5">
              <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" /> {t('gate.papercut.gapsTitle')}
              </div>
              {(cut.coverage_gaps || []).map((g, i) => (
                <div key={i} className="text-[11px] text-muted-foreground">
                  <span className="uppercase">[{g.severity || '?'}]</span> section {g.section ?? '?'}: {g.missing}
                </div>
              ))}
            </div>
          )}

          {noted > 0 && (
            <Button onClick={() => onSectionChanges(sectionNotes)} disabled={busy} variant="secondary" className="w-full">
              {busy ? <Spinner className="mr-1" /> : <Wrench className="mr-1 h-4 w-4" />}
              {t('gate.papercut.requestSections', { count: noted })}
            </Button>
          )}
        </>
      )}

      {/* Overall rework stays available even if the preview failed to load, so the
          producer is never stuck with only "Approve". */}
      <div className="mt-3 space-y-1.5 border-t border-primary/20 pt-2">
        <textarea
          value={overallNote}
          onChange={(e) => setOverallNote(e.target.value)}
          placeholder={t('gate.papercut.overallNote')}
          rows={2}
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] outline-none focus:border-primary"
        />
        <Button
          onClick={() => onRejectAll(overallNote.trim())}
          disabled={busy || !overallNote.trim()}
          variant="secondary"
          className="w-full"
        >
          {busy ? <Spinner className="mr-1" /> : <Wrench className="mr-1 h-4 w-4" />}
          {t('gate.papercut.rejectAll')}
        </Button>
      </div>
    </div>
  );
}
